import { sendRequest } from '../../../supplementary_components/requests';
import { addPopup } from '../../../supplementary_components/store';
import { userHasAccess } from '../../../supplementary_components/userDataDelegate';
import { Form } from '../Form';
import cloneDeep from 'lodash/cloneDeep';
import { showSuggestionsDropdown, hideSuggestionsDropdown } from './suggestionDropdownPosition';

/** When the text input is wrapped (e.g. calendar icon shell), nextSibling / previousElementSibling no longer point at options / label. */
function getSuggestionsDropdownEl(target, column) {
    const expectedId = column && column.name ? `${column.name}Options` : null;
    const next = target.nextSibling;
    if (expectedId && next && next.id === expectedId) return next;
    if (expectedId) {
        const byId = typeof document !== 'undefined' ? document.getElementById(expectedId) : null;
        if (byId) return byId;
    }
    return next;
}

function getFieldLabelEl(target, column) {
    const prev = target.previousElementSibling;
    const expectedLabelId = column && column.name ? `${column.name}Label` : null;
    if (expectedLabelId && prev && prev.id === expectedLabelId) return prev;
    if (expectedLabelId && typeof document !== 'undefined') {
        return document.getElementById(expectedLabelId);
    }
    return prev;
}

function setSuggestionsDropdownVisible(target, column, visible) {
    const optionsEl = getSuggestionsDropdownEl(target, column);
    if (!optionsEl) return;
    if (visible) {
        showSuggestionsDropdown(target, column, optionsEl);
    } else {
        hideSuggestionsDropdown(target, optionsEl);
    }
}

function setSuggestionsLoading(column, loading) {
    if (typeof column.onSuggestionsLoadingChange === 'function') {
        column.onSuggestionsLoadingChange(loading);
    }
}

export const columnAllowsAddNewButton = (column) => {
    if (!column?.addNewButton) return false;
    const access = column.addNewButton.configuration?.formAccessLevel;
    if (!access) return true;
    return userHasAccess(access);
};

export const setAndRemoveError = (column, target, message, setError) => {  
    
    const errorTag = document.getElementById(column.name+'Error');
    if (errorTag === null)return;
    if (setError){
        target.classList.add("error");
        errorTag.style.display = 'flex';
        errorTag.lastElementChild.innerText = `${message}`
    }else if((target.getAttribute('data-empty-error-applied') === 'true' && target.value !== '') || target.getAttribute('data-empty-error-applied') !== 'true'  ){
        target.classList.remove("error");
        errorTag.style.display = 'none';
    }else{
        // If submit button was pressed and input value is still null on outFocus
        target.classList.add("error");
        errorTag.style.display = 'flex';
        errorTag.lastElementChild.innerText = `This field cannot be empty`
    }
}

export const onFocusTrigger = (column, target, setSuggestionsList, abortControllerRef) =>{
    if(column.suggestionsEnabled){

        if(!column.suggestOnEachKey && target.value !== '' && target.getAttribute('data-edited') === "false"){
            return;
        }
        if(column.suggestionsListSource){
            const result = column.suggestionsListSource(target.value);
            if (result){
                setSuggestionsList(result);   
                setSuggestionsDropdownVisible(target, column, true);
            }
              
        }else{
            if(column.afterMinimumChars && column.afterMinimumChars > target.value.trim().length){
                setSuggestionsList([]); 
                setSuggestionsDropdownVisible(target, column, columnAllowsAddNewButton(column)); 
                return;
            }
            // Cancel previous request if any
            if (abortControllerRef && abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            
            // Create new controller
            const controller = new AbortController();
            if (abortControllerRef) {
                abortControllerRef.current = controller;
            }
            const wrapperTag = getFieldLabelEl(target, column);
            if(wrapperTag) wrapperTag.setAttribute("data-loading", "true");
            setSuggestionsLoading(column, true);
            const pattern = column.suggestOnEachKey
                ? encodeURIComponent(target.value.trim())
                : target.value.trim();
            sendRequest('GET', column.suggestionsLink+pattern, controller.signal, (value)=>{
                if(wrapperTag) wrapperTag.setAttribute("data-loading", "false");
                setSuggestionsLoading(column, false);
                if(value.length > 0){
                    setSuggestionsList(value); 
                }
                setSuggestionsDropdownVisible(target, column, value.length > 0);  
            }, (err)=>{
                if(wrapperTag) wrapperTag.setAttribute("data-loading", "false");
                setSuggestionsLoading(column, false);
            }, null);
        }
        
    }
}
export const onChange = (event, column, suggestionsList, setSuggestionsList, thisIsOnPaste, abortControllerRef)=>{
    
    if (column.filters){
        column.filters.forEach((filter)=>{
            event.target.value = filter(event.target.value);
        })
    }
    if (column.suggestionsEnabled){ 
        event.target.setAttribute('data-edited', "true");  
        if(column.afterMinimumChars && column.afterMinimumChars > event.target.value.trim().length){
            // event.target.nextSibling.style.display =  "none";
            if(setSuggestionsList)setSuggestionsList([]);
            setSuggestionsDropdownVisible(event.target, column, columnAllowsAddNewButton(column)); 
            return;
        }
        if(column.suggestionsListSource === undefined){
            const length = event.target.value.trim().length;
            const shouldFetch = column.suggestOnEachKey
                || thisIsOnPaste
                || suggestionsList.length > 0
                || (column.afterMinimumChars && column.afterMinimumChars + 1 > length)
                || (!column.afterMinimumChars && length > 2);
            if(shouldFetch){
                setSuggestionsDropdownVisible(event.target, column, true);
                if(suggestionsList){
                    const listClone = cloneDeep(suggestionsList);
                    listClone.push({loading:true});

                    setSuggestionsList(listClone);
                }
                if (column.suggestOnEachKey && abortControllerRef?.current) {
                    abortControllerRef.current.abort();
                }
                const controller = column.suggestOnEachKey ? new AbortController() : null;
                if (controller && abortControllerRef) {
                    abortControllerRef.current = controller;
                }
                const pattern = column.suggestOnEachKey
                    ? encodeURIComponent(event.target.value.trim())
                    : event.target.value.trim();
                sendRequest('GET', column.suggestionsLink+pattern, controller ? controller.signal : null, (value)=>{
                    setSuggestionsList(value);
                    setSuggestionsDropdownVisible(event.target, column, value.length > 0 || columnAllowsAddNewButton(column));  
                }, ()=>{
                    setSuggestionsList([]);
                }, null);
            }
        }else{
            const result = column.suggestionsListSource(event.target.value);
            if (result){
                setSuggestionsList(result);   
                setSuggestionsDropdownVisible(event.target, column, true);
            }
        }
    }
}

export const onBlurTrigger = (column, target, setSuggestionsList, abortControllerRef) =>{
    target.value = target.value.trim();
    const wrapperTag = getFieldLabelEl(target, column);
    if(column.suggestionsEnabled){
        if (abortControllerRef && abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
            const loadingEl = getFieldLabelEl(target, column);
            if(loadingEl) loadingEl.setAttribute("data-loading", "false");
            setSuggestionsLoading(column, false);
        }
        let selected = false;
        const optionsEl = getSuggestionsDropdownEl(target, column);
        if (optionsEl && optionsEl.childNodes[0]) {
        for (const child of optionsEl.childNodes[0].childNodes){
            if(child.matches(':hover')){
                const parsedRawVal = JSON.parse(child.getAttribute("data-raw-val"))
                if(parsedRawVal && parsedRawVal[column.availableOptionDefiningColumnName] === false){
                    target.focus();
                    return
                }
                if(child.id === 'loadingIcon'){
                    target.value = '';
                    hideSuggestionsDropdown(target, getSuggestionsDropdownEl(target, column));
                    setSuggestionsList([]);
                    return;
                }
                if(child.id === 'addButton'){
                    if (!columnAllowsAddNewButton(column)) {
                        target.value = '';
                        hideSuggestionsDropdown(target, getSuggestionsDropdownEl(target, column));
                        return;
                    }
                    const postAddingAction = (value) => {
                        target.value = column.displayValueGenerator(value);
                        target.setAttribute('data-value', JSON.stringify(value));
                        target.setAttribute('data-edited', 'true');
                        target.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                    addPopup({content:Form, popupData:{configuration: column.addNewButton.configuration, prefill: column.prefillConditioning(target.value)}, postSuccessAction:postAddingAction});
                    target.value = '';
                    hideSuggestionsDropdown(target, getSuggestionsDropdownEl(target, column));
                    return;
                }
                selected = true;
                if(column.customOnSelectMethod){
                    target.value = "";
                    column.customOnSelectMethod(parsedRawVal);
                }else{
                    target.value = column.displayValueGenerator?column.displayValueGenerator(parsedRawVal):child.innerText;
                    // target.value = child.innerText;
                    if(column.afterSelection){
                        const doTheDefaultAction = column.afterSelection(child.getAttribute('data-raw-val'));
                        if(doTheDefaultAction){
                            target.setAttribute('data-value', child.getAttribute('data-raw-val'));
                            setAndRemoveError(column, target, null, false);
                        }
                    }else{
                        target.setAttribute('data-value', child.getAttribute('data-raw-val'))
                        setAndRemoveError(column, target, null, false);
                    }
                }
            }
        }
        }
        
        
        if (!selected && !column.noValidation){
            
            if(target.value !== ''){
                let itemName = target.value;
                if(column.displayValueToRawValue){
                    itemName = column.displayValueToRawValue(target.value); 
                }
                if(wrapperTag)wrapperTag.setAttribute("data-loading", "true");
                sendRequest('GET', column.suggestionsLink+itemName, null, (value)=>{
                    for (const entry of value){
                        if (column.displayValueGenerator(entry).trim() === target.value){
                            
                            if(entry[column.availableOptionDefiningColumnName] === false){
                                target.value = '';
                                if(wrapperTag)wrapperTag.setAttribute("data-loading", "false");
                                return;
                            }
                            selected = true;
                            const entryJson = JSON.stringify(entry);
                            if(column.afterSelection){
                                const doTheDefaultAction = column.afterSelection(entryJson);
                                if(doTheDefaultAction){
                                    target.setAttribute('data-value', entryJson);
                                    setAndRemoveError(column, target, null, false);
                                }
                            }else{
                                target.setAttribute('data-value', entryJson)
                            }
                            if(column.checksMust){
                                conductCheck(column, target);
                            }
                            if(wrapperTag)wrapperTag.setAttribute("data-loading", "false")
                            return;
                        }
                        if(column.rawValComparationFieldName && String(entry[column.rawValComparationFieldName]) === String(target.value)){
                            target.value = column.displayValueGenerator(entry).trim();
                            const entryJson = JSON.stringify(entry);
                            if(column.afterSelection){
                                const doTheDefaultAction = column.afterSelection(entryJson);
                                if(doTheDefaultAction){
                                    target.setAttribute('data-value', entryJson);
                                    setAndRemoveError(column, target, null, false);
                                }
                            }else{
                                target.setAttribute('data-value', entryJson);
                            }
                            selected = true;
                            if(column.checks){
                                if(wrapperTag)wrapperTag.setAttribute("data-loading", "true");
                                conductCheck(column, target);
                            }
                            return;
                        }
                    } 
                    if(!selected){
                        target.value = '';
                        target.setAttribute('data-value', '');
                        if(column.onSuggestionCleared){
                            column.onSuggestionCleared(target);
                        }
                    }else{
                        setAndRemoveError(column, target, null, false);
                    }
                    if(wrapperTag)wrapperTag.setAttribute("data-loading", "false");
                },()=>{
                    if(wrapperTag)wrapperTag.setAttribute("data-loading", "false")
                }, null);
            }         
        }
        if(target.value === ''){
            target.setAttribute('data-value', '');
            if(column.onSuggestionCleared){
                column.onSuggestionCleared(target);
            }
        }
        hideSuggestionsDropdown(target, getSuggestionsDropdownEl(target, column));
    }
    
    if(column.checks){
        if (target.value !== ''){
            if(!column.checksMust){
                conductCheck(column, target);
            }
        }else{
            setAndRemoveError(column, target, null, false, true);
        }
    }else{
        if(!column.suggestionsEnabled){
            setAndRemoveError(column, target, null, false);
        }
    }
    if(column.onblur){
        if (target.value !== ''){
            target.value = column.onblur(target.value, target)
        }
    }
}
// export const onPasteTrigger = (column, target) =>{
//     if (column.onblur && target.value !== ''){
//         setTimeout(() => {
//             target.value = column.onblur(target.value)
//         }, 10);
//     }
// }
export const conductCheck = (column, target) => {
    //submit
    // target.parentElement.parentElement.parentElement.parentElement
    let submitButton = null;
    try {
        const form = target.parentElement.parentElement
        if(form.tagName === "FORM"){
            // const formId = form.id;
            submitButton = document.querySelector(`input[type="submit"][form="${form.id}"]`);
            submitButton.disabled = true;
            submitButton.parentElement.classList += ' disabled'
        }
    } catch (error) {
        
    }
    const wrapperTag = getFieldLabelEl(target, column);
    if (wrapperTag) wrapperTag.setAttribute("data-loading", "true");
    const listOfMixedPromisesAndValues = column.checks.map(func => func(target.value, column, target));
    Promise.all(listOfMixedPromisesAndValues).then(result => {
        setAndRemoveError(column, target, null, false);
        result.forEach((message)=>{
            if (message){
                setAndRemoveError(column, target, message, true);
            }
            if (wrapperTag) wrapperTag.setAttribute("data-loading", "false");
            if(submitButton){
                submitButton.disabled = false;
                submitButton.parentElement.classList.remove('disabled');
            }
        })
    }).catch(() => {
        if (wrapperTag) wrapperTag.setAttribute("data-loading", "false");
    });
}